import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogContent,
  Typography,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import propertyService from '../../services/propertyService';
import { ImageInspectionRegistry } from '../../models/property';

interface RoomImagesAccordionProps {
  registry: ImageInspectionRegistry;
  propertyId: string;
}

const RoomImagesAccordion: React.FC<RoomImagesAccordionProps> = ({ registry, propertyId }) => {
  const [roomImages, setRoomImages] = useState<{ [room: string]: { url: string; Room: string }[] }>({});
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const handleFetchRoomImages = async (room: string) => {
    if (roomImages[room]) return;

    try {
      const imagesForRoom = registry.data?.filter((item) => item.Room === room) || [];

      const images = await Promise.all(
        imagesForRoom.map(async (image) => ({
          url: image.imageId ? await propertyService.getPropertyImageUrl(propertyId, image.imageId) : '',
          Room: image.Room,
        }))
      );

      setRoomImages((prev) => ({ ...prev, [room]: images }));
    } catch (error) {
      console.error(`Error fetching images for room: ${room}`, error);
    }
  };

  const handleImageClick = (image: string, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!selectedImage || currentIndex === null) return;
    const room = Object.keys(roomImages).find((room) =>
      roomImages[room]?.some((image, index) => index === currentIndex)
    );
    if (!room) return;

    const images = roomImages[room];
    const newIndex =
      direction === 'prev'
        ? (currentIndex - 1 + images.length) % images.length
        : (currentIndex + 1) % images.length;

    setSelectedImage(images[newIndex].url);
    setCurrentIndex(newIndex);
  };

  const rooms = Array.from(new Set(registry.data?.map((item) => item.Room) || []));

  return (
    <>
      {rooms.sort().map((room) => (
        <Accordion key={room} onChange={() => handleFetchRoomImages(room)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {room} ({registry.data?.filter((item) => item.Room === room).length || 0} images)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {roomImages[room]?.map((image, index) => (
                <Box
                  key={index}
                  component="img"
                  src={image.url}
                  alt={`${room} Image ${index + 1}`}
                  sx={{ width: 100, height: 100, objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => handleImageClick(image.url, index)}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={Boolean(selectedImage)} onClose={() => setSelectedImage(null)} fullWidth maxWidth="md">
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src={selectedImage || ''}
              alt="Selected"
              sx={{ width: '100%' }}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                onClick={() => navigateImage('prev')}
                disabled={!Object.keys(roomImages).length}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                onClick={() => navigateImage('next')}
                disabled={!Object.keys(roomImages).length}
              >
                Next
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RoomImagesAccordion;
